import { useMemo } from 'react';

import { useQuery } from '@apollo/react-hooks';
import { isNil } from 'lodash-es';

import {
  GetUserOrdersQuery,
  IGetUserOrders,
  IGetUserOrdersInput
} from 'remote/queries/user-orders';
import { UserDetails } from 'state/auth/hooks/use-current-user';
import { stripTypename } from 'state/graphql/links/strip-typename';
import { OrderStatus } from 'state/order';

/** Tech Debt
 * This isn't using our generated GraphQL queries & types.
 * Replace `useQuery<...` with `useGetUserOrdersQuery` and update types in many places.
 * There will be less usage of IHistoricalOrder and more usage of IUserOrderFragment.
 * The Support V2 Orders List has a very similar query that should return the same orders as this.
 * */
export const useUserOrders = (user: UserDetails | null, variables?: IGetUserOrdersInput) => {
  const { data, error, loading, refetch } = useQuery<IGetUserOrders, IGetUserOrdersInput>(
    GetUserOrdersQuery,
    {
      variables: {
        orderStatuses: [
          OrderStatus.INSERT_SUCCESSFUL,
          OrderStatus.UPDATE_SUCCESSFUL,
          OrderStatus.CATERING_PLACED,
          OrderStatus.CATERING_SUCCESSFUL,
          OrderStatus.REFUND_SUCCESSFUL,
          OrderStatus.REFUND_ERROR,
          OrderStatus.REFUND_REQUESTED
        ],
        shouldFilterByRegion: true,
        ...variables
      },
      skip: isNil(user),
      context: { shouldNotBatch: true }
    }
  );

  const orders = useMemo(
    () => (data && data.userOrders && user ? data.userOrders.orders.map(stripTypename) : []),
    [data, user]
  );

  return { error, loading, orders, refetch: user ? refetch : () => Promise.resolve() };
};
